"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { cn } from "@/lib/utils";

// Типы данных для элемента FAQ
interface FaqItem {
  question: string;
  answer: string;
}

// Данные вопросов и ответов
const faqItems: FaqItem[] = [
  {
    question: "Мне это точно нужно? Я и так веду учёт в Excel.",
    answer:
      "Excel не обновляется автоматически. Комиссии, хранение, логистика, штрафы — всё меняется каждый день. MPUNIT синхронизирует 15+ типов данных с WB и Ozon автоматически. Вы видите актуальную прибыль по каждому товару, а не вчерашние цифры.",
  },
  {
    question: "А если отчёт WB ещё не пришёл?",
    answer:
      "Отчёт о реализации WB приходит с задержкой 2-3 недели. MPUNIT не ждёт — строит предварительные данные сразу. В интерфейсе видно, что цифры предварительные. Когда отчёт придёт — всё пересчитается автоматически.",
  },
  {
    question: "Это безопасно? Вы не сломаете мой магазин?",
    answer:
      "Мы используем только read-only доступ к API — физически не можем ничего изменить в вашем ЛК. API-ключи зашифрованы. Мы только читаем данные, ничего не трогаем.",
  },
  {
    question: "У меня несколько магазинов на разных площадках.",
    answer:
      "Подключайте сколько нужно — несколько кабинетов WB и Ozon на одном дашборде. Видите общую картину и каждый магазин отдельно.",
  },
  {
    question: "₽1 990/мес — это ещё один расход. Оно окупится?",
    answer:
      "Один убыточный артикул, который вы продаёте не зная что он в минусе, теряет в среднем ₽10 000-50 000/мес. MPUNIT покажет его за 15 минут после подключения. Тариф окупается с первого найденного убыточного товара.",
  },
  {
    question: "Можно попробовать перед оплатой?",
    answer:
      "Да, 30 дней полного доступа бесплатно. Без привязки карты. Регистрация через email, ВКонтакте или Яндекс.",
  },
];

export function FAQ() {
  // Индекс открытого элемента (null — все закрыты)
  const [openItem, setOpenItem] = useState<number | null>(null);

  // Переключение элемента: повторный клик закрывает, новый — открывает
  function handleToggle(index: number): void {
    setOpenItem((prev) => (prev === index ? null : index));
  }

  return (
    <section id="faq" className="py-24 px-6 border-t border-white/[0.06]">
      <div className="max-w-2xl mx-auto">
        <ScrollReveal direction="up">
          {/* Заголовок секции */}
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-purple-400 mb-3">
              Вопросы
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Частые вопросы
            </h2>
          </div>
        </ScrollReveal>

        {/* Список аккордеона */}
        <div>
          {faqItems.map((item, index) => {
            const isOpen = openItem === index;

            return (
              <ScrollReveal key={index} direction="up" delay={index * 0.05}>
                {/* Разделитель — только нижняя граница, без карточки */}
                <div className={cn("border-b border-white/[0.06]")}>
                  {/* Триггер — кнопка вопроса */}
                  <button
                    type="button"
                    onClick={() => handleToggle(index)}
                    className="py-5 flex justify-between items-center w-full cursor-pointer"
                  >
                    <span className="text-base font-medium text-white text-left">
                      {item.question}
                    </span>

                    {/* Иконка плюс/минус — нейтральный цвет */}
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="ml-4 flex-shrink-0"
                    >
                      {isOpen ? (
                        <Minus size={20} className="text-white/60" />
                      ) : (
                        <Plus size={20} className="text-white/60" />
                      )}
                    </motion.span>
                  </button>

                  {/* Контент ответа с Framer Motion анимацией высоты */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <p className="pb-5 text-sm text-white/60 leading-relaxed">
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
